import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Copy, ExternalLink, FlaskConical, Play, Loader2, CheckCircle2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { PillTabs } from '../common/Modal';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Recommendation, getTypeLabel } from '../../pages/RecommendationsPage';
import { CHART_COLORS, chartTickStyle, chartTooltipStyle } from '../../utils/chartTheme';
import { getActiveTenantId } from '../../services/tenantContext';
import { StepTimeline, useInvestigationSocket } from '../investigation/StepTimeline';
import { StateCatalogue, stateDef } from './RecommendationLifecycle';
import { HowToFixTab, makeFixPlanFeedbackSender, makeFixPlanFetcher, makeFixPlanMarkdownFetcher } from './HowToFixTab';

const OPTIMIZATION_API_URL = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';
const COST_API_URL = import.meta.env.VITE_COST_API_URL ? `${import.meta.env.VITE_COST_API_URL}/api/v1` : 'http://localhost:3002/api/v1';

// The live pipeline timeline, its socket hook and the step-label map now live
// in one shared module so this panel and the anomaly RCA tab render an
// identical stepper - see components/investigation/StepTimeline.tsx.


interface ResourceType {
  resourceType: string;
}

/** Mirrors idle-resource.service.ts's own service-name matching exactly -
 * see that file's comment for why NAT Gateway matching isn't guaranteed
 * exhaustive across every CUR export configuration. */
function inferResourceType(service: string): ResourceType['resourceType'] {
  const s = service || '';
  if (/redshift/i.test(s)) return 'redshift';
  if (/relational database|rds/i.test(s)) return 'rds';
  if (/elasticache/i.test(s)) return 'elasticache';
  if (/elastic load balanc/i.test(s)) return 'elb';
  if (/nat gateway/i.test(s)) return 'nat_gateway';
  if (/elastic block store|\bebs\b/i.test(s)) return 'ebs';
  return 'ec2';
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  // Same fix as the socket above. This one sent x-tenant-id: '' on the
  // Metrics and Changes tab requests - the only two calls in the app not
  // routed through getActiveTenantId() - which is why those two tabs failed
  // while everything else on the same page worked.
  const tenantId = getActiveTenantId();
  return { 'x-tenant-id': tenantId, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

// Module-level so its identity is stable (it is an effect dependency in HowToFixTab).
const fetchFixPlan = makeFixPlanFetcher(OPTIMIZATION_API_URL, authHeaders);
const fetchFixPlanMarkdown = makeFixPlanMarkdownFetcher(OPTIMIZATION_API_URL, authHeaders);
const sendFixPlanFeedback = makeFixPlanFeedbackSender(OPTIMIZATION_API_URL, authHeaders);

interface RelatedResource {
  id: string;
  resource_id: string;
  resource_name?: string;
  type: string;
  current_cost: number;
  potential_savings: number;
  status: string;
}

type ChangesResult =
  | { available: true; events: { eventId: string; eventName: string; eventTime: string; username?: string }[] }
  | { available: false; reason: string; message: string };

interface RecommendationDetailBodyProps {
  rec: Recommendation;
  phase?: 'classifying' | 'generating' | 'confirming';
  onInvestigate: (id: string) => void;
  onConfirm: (id: string) => void;
  isBusy: boolean;
  /** Phase 4.0 state catalogue - decides which Investigation buttons apply. */
  catalogue: StateCatalogue;
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'fix', label: 'How to fix' },
  { key: 'investigation', label: 'Investigation' },
  { key: 'metrics', label: 'Metrics' },
  { key: 'changes', label: 'Changes' },
  { key: 'related', label: 'Related' },
];

/**
 * The tabbed investigation content for one recommendation - deliberately NOT
 * wrapped in a Modal/dialog here. This used to render inside a popup overlay;
 * it's now hosted by RecommendationDetailPage as a full route
 * (/recommendations/:id) instead, matching a real product's pattern of a
 * dedicated investigation page rather than a floating dialog for something
 * this information-dense (5 tabs, a live pipeline, charts).
 */
export const RecommendationDetailBody: React.FC<RecommendationDetailBodyProps> = ({
  rec,
  phase,
  onInvestigate,
  onConfirm,
  isBusy,
  catalogue,
}) => {
  const [activeTab, setActiveTab] = useState<string>('overview');
  const approval = rec.metadata?.approval_action;
  const artifact = approval?.artifact;
  const approvalOutcomeState = approval?.state as 'previewed' | 'completed' | 'failed' | undefined;
  const tier = approval?.classification?.tier;
  // Phase 4.0: what the Investigation tab offers follows the item's state.
  //   Open / In review          -> Run (or re-run) investigation
  //   In review / Accepted with a preview -> Execute (L1) / Raise ticket
  //   anything later            -> show the state; the state bar has the moves
  const canInvestigate = rec.status === 'open' || rec.status === 'in_review';
  const canConfirm = approvalOutcomeState === 'previewed' && (rec.status === 'in_review' || rec.status === 'accepted');
  const lastAttemptFailed = approvalOutcomeState === 'failed';

  // Connects as soon as the Investigation tab is opened - NOT gated on
  // isBusy/phase. A fresh authenticated socket (JWT verify + tenant
  // authorization lookup) takes longer to establish than the orchestrator's
  // real steps take to run, so waiting until the button click to start
  // connecting meant the connection often finished AFTER the server had
  // already emitted most or all of the step events - they'd arrive in one
  // burst instead of live, one at a time (confirmed: this was happening).
  // Connecting eagerly, the moment the tab opens, means the socket is
  // already subscribed and listening well before any click, so real step
  // events land as they actually happen.
  const socketEnabled = activeTab === 'investigation';
  const steps = useInvestigationSocket(rec.id, socketEnabled);

  // `undefined` = not fetched yet (fetch once, on first tab visit).
  // `null` (metrics/changes) = fetched, genuinely nothing there - a real,
  // distinct outcome from "haven't tried" or "errored", never collapsed into
  // the same sentinel. Using one value for both "not tried" and "tried, got
  // nothing" was a real bug here: the effect's own guard re-read that same
  // falsy value as "still need to fetch" and fired again forever, so a
  // resource with no real data just showed "Loading..." indefinitely instead
  // of ever reaching an end state.
  const [metrics, setMetrics] = useState<any>(undefined);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [changes, setChanges] = useState<ChangesResult | undefined>(undefined);
  const [changesLoading, setChangesLoading] = useState(false);
  const [related, setRelated] = useState<RelatedResource[] | undefined>(undefined);
  const [relatedLoading, setRelatedLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== 'metrics' || metrics !== undefined || metricsLoading) return;
    setMetricsLoading(true);
    axios
      .post(
        `${COST_API_URL}/cost/resource-metrics`,
        { provider: rec.provider, resourceType: inferResourceType(rec.service), resources: [{ resource_id: rec.resource_id, region: rec.region }] },
        { headers: authHeaders() }
      )
      .then((res) => {
        setMetrics(res.data?.data?.[0] ?? null);
        setMetricsError(null);
      })
      .catch((err) => {
        // Previously this swallowed the error whole and rendered a flat
        // "Could not load metrics", which made a tenant/auth failure
        // indistinguishable from a resource that genuinely has no CloudWatch
        // data. Those need different fixes, so say which one happened.
        setMetrics(null);
        const status = err?.response?.status;
        setMetricsError(status ? `${status} ${err.response?.data?.message || err.message}` : err.message);
      })
      .finally(() => setMetricsLoading(false));
  }, [activeTab, metrics, metricsLoading, rec]);

  useEffect(() => {
    if (activeTab !== 'changes' || changes !== undefined || changesLoading || rec.provider !== 'aws') return;

    // Some findings are not about a single resource at all — a Savings Plan
    // purchase recommendation applies to an account's whole compute usage and
    // carries no account_id/region. Calling the changes endpoint without those
    // returned a bare "Request failed with status code 400", which looks like a
    // broken page rather than a category that legitimately has no change
    // history. Answer the question honestly instead of asking it.
    if (!rec.account_id || !rec.region) {
      setChanges({
        available: false,
        reason: 'not_applicable',
        message:
          'This recommendation is not scoped to a single resource — it applies to the account as a whole, so there is no resource-level change history to show.',
      });
      return;
    }

    setChangesLoading(true);
    axios
      .get(`${COST_API_URL}/resources/${encodeURIComponent(rec.resource_id)}/changes`, {
        params: { accountId: rec.account_id, region: rec.region },
        headers: authHeaders(),
      })
      .then((res) => setChanges(res.data?.data ?? { available: false, reason: 'error', message: 'Unexpected response' }))
      .catch((err) => setChanges({ available: false, reason: 'error', message: err.message }))
      .finally(() => setChangesLoading(false));
  }, [activeTab, changes, changesLoading, rec]);

  useEffect(() => {
    if (activeTab !== 'related' || related !== undefined || relatedLoading) return;
    setRelatedLoading(true);
    axios
      .get(`${OPTIMIZATION_API_URL}/api/v1/recommendations/${rec.id}/related`, { headers: authHeaders() })
      .then((res) => setRelated(res.data?.data ?? []))
      .catch(() => setRelated([]))
      .finally(() => setRelatedLoading(false));
  }, [activeTab, related, relatedLoading, rec.id]);

  const chartData = useMemo(() => {
    if (!metrics?.cpu_series) return [];
    return metrics.cpu_series.map((p: { timestamp: string; value: number }) => ({
      time: new Date(p.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      value: Math.round(p.value * 10) / 10,
    }));
  }, [metrics]);

  return (
    <div>
      <PillTabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {activeTab === 'overview' && (
        <>
          <Card className="mb-3 p-4">
            <p className="text-sm text-foreground/80">{rec.recommendation}</p>
          </Card>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 mb-3">
            <Card className="p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Current Cost</div>
              <div className="mt-1 text-sm font-semibold text-foreground">
                {rec.current_cost_formatted || `$${rec.current_cost.toFixed(2)}`}
              </div>
            </Card>
            <Card className="p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Potential Savings</div>
              <div className="mt-1 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                {rec.potential_savings_formatted || `$${rec.potential_savings.toFixed(2)}`}
              </div>
            </Card>
            <Card className="p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Service</div>
              <div className="mt-1 text-sm font-medium text-foreground">{rec.service}</div>
            </Card>
            <Card className="p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Account</div>
              <div className="mt-1 text-sm font-medium text-foreground">
                {rec.account_id || <span className="text-muted-foreground/70">Unknown</span>}
              </div>
            </Card>
            <Card className="p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Region</div>
              <div className="mt-1 text-sm font-medium text-foreground">
                {rec.region || <span className="text-muted-foreground/70">Unknown</span>}
              </div>
            </Card>
          </div>
          <Card className="p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Reason</div>
            <div className="mt-1 text-sm text-foreground">{rec.reason}</div>
            {rec.metadata?.reason_code && (
              <span className="mt-1.5 inline-block rounded bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary dark:bg-primary/10 dark:text-primary">
                {rec.metadata.reason_code}
              </span>
            )}
          </Card>
        </>
      )}

      {/* Kept mounted after the first visit so ticked checklist items survive tab switches. */}
      <div className={activeTab === 'fix' ? '' : 'hidden'}>
        <HowToFixTab recId={rec.id} active={activeTab === 'fix'} fetchPlan={fetchFixPlan} fetchMarkdown={fetchFixPlanMarkdown} sendFeedback={sendFixPlanFeedback} />
      </div>

      {activeTab === 'investigation' && (
        <div>
          {steps.length === 0 && phase && (
            <Card className="mb-3 p-4">
              <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Starting investigation…
              </span>
            </Card>
          )}
          {steps.length > 0 && (
            <Card className="mb-3 p-4">
              <div className="mb-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Live progress</div>
              <StepTimeline steps={steps} />
            </Card>
          )}

          <div className="mb-3 flex flex-wrap items-center gap-2">
            {canConfirm && (
              <Button size="sm" variant="primary" onClick={() => onConfirm(rec.id)} disabled={isBusy}>
                <Play />
                {tier === 'L1' ? 'Execute' : 'Raise Ticket'}
              </Button>
            )}
            {canInvestigate && (
              <Button size="sm" variant="outline" onClick={() => onInvestigate(rec.id)} disabled={isBusy}>
                <FlaskConical />
                {lastAttemptFailed ? 'Retry investigation' : approvalOutcomeState === 'previewed' ? 'Re-run investigation' : 'Run investigation'}
              </Button>
            )}
            {!canInvestigate && !canConfirm && (
              <span className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground">
                <CheckCircle2 className="h-4 w-4" /> {stateDef(catalogue, rec.status).label} - next steps are in the state bar above
              </span>
            )}
            {lastAttemptFailed && approval?.errors?.length > 0 && (
              <span className="text-xs text-rose-600">Last attempt failed: {approval.errors.map((e: any) => e.message).join('; ')}</span>
            )}
            {isBusy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/70" />}
          </div>

          {approval && (
            <Card className="p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Investigation Outcome</div>
              <div className="text-sm mb-2">
                <span className="font-medium text-foreground">{approval.classification?.tier}</span>
                <span className="text-muted-foreground"> — {approval.classification?.reasoning}</span>
              </div>
              {approval.l1_execution && (
                <div className="text-sm mb-2">
                  <span className={`font-medium ${approval.l1_execution.status === 'executed' ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
                    {approval.l1_execution.status === 'executed' ? 'Executed automatically' : `Not executed (${approval.l1_execution.status})`}
                  </span>
                  {approval.l1_execution.block_reason && <span className="text-muted-foreground"> — {approval.l1_execution.block_reason}</span>}
                </div>
              )}
              {approval.ticket && (
                <div className="text-sm mb-2">
                  <span className="text-muted-foreground">Ticket: </span>
                  {approval.ticket.external_url ? (
                    <a href={approval.ticket.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:text-primary dark:text-primary">
                      {approval.ticket.external_key || 'open'} <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground">{approval.ticket.status}</span>
                  )}
                </div>
              )}
              {artifact?.script_text && (
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Runnable script</span>
                    <button onClick={() => navigator.clipboard.writeText(artifact.script_text || '')} className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary dark:text-primary">
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  </div>
                  <pre className="mt-1 max-h-48 overflow-y-auto overflow-x-auto whitespace-pre-wrap rounded bg-gray-900 p-3 text-xs text-muted-foreground/70">{artifact.script_text}</pre>
                </div>
              )}
              {artifact?.sop && !artifact.sop.error && (
                <div className="mt-2 text-sm">
                  <div className="font-medium text-foreground">{artifact.sop.title}</div>
                  <div className="mt-1 text-foreground/80">{artifact.sop.problem_summary}</div>
                  {/* The SOP's steps used to reach only the ticket; shown here too (plan 4A-8). */}
                  {([
                    ['Confirm the problem', artifact.sop.identification_steps],
                    ['Fix', artifact.sop.resolution_steps],
                    ['Check it worked', artifact.sop.verification_steps],
                  ] as [string, string[] | undefined][]).filter(([, s]) => Array.isArray(s) && s.length > 0).map(([label, s]) => (
                    <div key={label} className="mt-2">
                      <div className="text-xs font-medium text-muted-foreground">{label}</div>
                      <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-foreground/90">
                        {(s as string[]).map((step, i) => <li key={i}>{step}</li>)}
                      </ol>
                    </div>
                  ))}
                  <p className="mt-2 text-xs text-muted-foreground">AI-written guide. The reviewed, step-by-step plan with commands is in the "How to fix" tab.</p>
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {activeTab === 'metrics' && (
        <Card className="p-4">
          {metricsLoading && <p className="text-sm text-muted-foreground">Loading metrics…</p>}
          {!metricsLoading && metrics === null && (
            <p className="text-sm text-muted-foreground">
              {metricsError
                ? `Could not load metrics for this resource: ${metricsError}`
                : 'No utilization metrics are published for this resource type.'}
            </p>
          )}
          {metrics && chartData.length > 0 && (
            <>
              {/* Derived from the series rather than hardcoded. This label read
                  "14-day" while the API was returning 504 hourly points - 21
                  days - and the x-axis visibly spanned three weeks. A caption
                  that disagrees with the axis directly beneath it undermines
                  every other number on the page. */}
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                CPU utilization ({Math.round(chartData.length / 24)}-day, hourly)
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <XAxis dataKey="time" tick={chartTickStyle} interval={Math.max(0, Math.floor(chartData.length / 8))} />
                  <YAxis tick={chartTickStyle} unit="%" />
                  <Tooltip contentStyle={chartTooltipStyle} />
                  <Line type="monotone" dataKey="value" stroke={CHART_COLORS.primary} dot={false} />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                <span>Avg: {Number(metrics.cpu_avg ?? 0).toFixed(1)}%</span>
                <span>P95: {Number(metrics.cpu_p95 ?? 0).toFixed(1)}%</span>
              </div>
            </>
          )}
          {metrics && chartData.length === 0 && metrics.idle_signal_name && (
            <p className="text-sm text-foreground/80">
              {metrics.idle_signal_name}: {metrics.idle_signal_value}
            </p>
          )}
          {metrics && chartData.length === 0 && !metrics.idle_signal_name && (
            <p className="text-sm text-muted-foreground">No time-series metrics available for this resource type.</p>
          )}
        </Card>
      )}

      {activeTab === 'changes' && (
        <Card className="p-4">
          {rec.provider !== 'aws' && <p className="text-sm text-muted-foreground">Change history is currently AWS-only (CloudTrail-based).</p>}
          {rec.provider === 'aws' && changesLoading && <p className="text-sm text-muted-foreground">Loading recent activity…</p>}
          {rec.provider === 'aws' && changes && !changes.available && changes.reason === 'permission_missing' && (
            <p className="text-sm text-amber-700 dark:text-amber-400">{changes.message}</p>
          )}
          {rec.provider === 'aws' && changes && !changes.available && changes.reason !== 'permission_missing' && (
            <p className="text-sm text-muted-foreground">{changes.message}</p>
          )}
          {rec.provider === 'aws' && changes && changes.available && changes.events.length === 0 && (
            <p className="text-sm text-muted-foreground">No recent CloudTrail activity on this resource in the last 14 days.</p>
          )}
          {rec.provider === 'aws' && changes && changes.available && changes.events.length > 0 && (
            <div className="space-y-2">
              {changes.events.map((e) => (
                <div key={e.eventId} className="flex items-center justify-between border-b border-border pb-1.5 text-sm last:border-0 dark:border-border">
                  <span className="font-medium text-foreground">{e.eventName}</span>
                  <span className="text-xs text-muted-foreground">
                    {e.username ? `${e.username} · ` : ''}
                    {new Date(e.eventTime).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {activeTab === 'related' && (
        <Card className="p-4">
          {relatedLoading && <p className="text-sm text-muted-foreground">Loading related resources…</p>}
          {related && related.length === 0 && <p className="text-sm text-muted-foreground">No other active recommendations in this account/service.</p>}
          {related && related.length > 0 && (
            <div className="space-y-2">
              {related.map((r) => (
                <div key={r.id} className="flex items-center justify-between border-b border-border pb-1.5 text-sm last:border-0 dark:border-border">
                  <div>
                    <span className="font-medium text-foreground">{r.resource_name || r.resource_id}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{getTypeLabel(r.type)}</span>
                  </div>
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">save ${Number(r.potential_savings ?? 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
};
